import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GartnerHypeCycle from '../components/GartnerHypeCycle';
import { 
  Loader2, 
  ChartBar, 
  Bookmark, 
  TrendingUp, 
  ArrowRight,
  Sparkles,
  Target,
  Trash2,
  ExternalLink,
  Calendar,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  User,
  Clock,
  Activity,
  LogIn,
  Award,
  Zap,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { assessmentsAPI, savedUseCasesAPI, userStatsAPI, useCasesAPI } from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const UserDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [assessments, setAssessments] = useState([]);
  const [savedUseCases, setSavedUseCases] = useState([]);
  const [allUseCases, setAllUseCases] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [loadingUseCases, setLoadingUseCases] = useState(true);
  const [showCompare, setShowCompare] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !isAuthenticated && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location.state]);

  // Fetch user's assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      if (!user?.user_id) return;
      
      try {
        const data = await assessmentsAPI.getMyAssessments();
        setAssessments(data || []);
      } catch (error) {
        console.error('Failed to fetch assessments:', error);
      } finally {
        setLoadingAssessments(false);
      }
    };

    if (user) {
      fetchAssessments();
    }
  }, [user]);

  // Fetch saved use cases
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const data = await savedUseCasesAPI.getSavedWithDetails();
        setSavedUseCases(data || []);
      } catch (error) {
        console.error('Failed to fetch saved use cases:', error);
      } finally {
        setLoadingSaved(false);
      }
    };

    if (user) {
      fetchSaved();
    }
  }, [user]);

  // Fetch all use cases for Gartner Hype Cycle
  useEffect(() => {
    const fetchAllUseCases = async () => {
      try {
        const data = await useCasesAPI.getAll();
        setAllUseCases(data || []);
      } catch (error) {
        console.error('Failed to fetch all use cases:', error);
      } finally {
        setLoadingUseCases(false);
      }
    };

    if (user) {
      fetchAllUseCases();
    }
  }, [user]);

  // Fetch user stats
  useEffect(() => {
    const fetchUserStats = async () => {
      if (!user?.user_id) return;
      
      try {
        const stats = await userStatsAPI.getMyStats();
        setUserStats(stats);
      } catch (error) {
        console.error('Failed to fetch user stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    if (user) {
      fetchUserStats();
    }
  }, [user]);

  const handleUnsave = async (usecaseId) => {
    try {
      await savedUseCasesAPI.unsave(usecaseId);
      setSavedUseCases(prev => prev.filter(uc => uc.id !== usecaseId));
    } catch (error) {
      console.error('Failed to unsave:', error);
    }
  };

  const currentUser = user || location.state?.user;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  // Prepare chart data from assessments (oldest first for progression)
  const chartData = [...assessments].reverse().map((assessment, index) => ({
    name: formatShortDate(assessment.created_at),
    score: assessment.overall_score,
    company: assessment.company || `Quickscan ${index + 1}`,
    fullDate: formatDate(assessment.created_at)
  }));

  // Prepare dimension comparison data for bar chart
  const getDimensionComparisonData = () => {
    if (assessments.length < 2) return [];
    
    const latest = assessments[0];
    const previous = assessments[1];
    
    if (!latest?.dimension_scores || !previous?.dimension_scores) return [];
    
    return latest.dimension_scores.map((dim, index) => ({
      name: dim.dimension.length > 12 ? dim.dimension.substring(0, 12) + '...' : dim.dimension,
      fullName: dim.dimension,
      latest: dim.score,
      previous: previous.dimension_scores[index]?.score || 0,
      change: dim.score - (previous.dimension_scores[index]?.score || 0)
    }));
  };

  const dimensionData = getDimensionComparisonData();

  // Calculate progress between latest and previous assessment
  const getProgressIndicator = () => {
    if (assessments.length < 2) return null;
    const latest = assessments[0]?.overall_score || 0;
    const previous = assessments[1]?.overall_score || 0;
    const diff = latest - previous;
    
    if (diff > 0) return { icon: ArrowUpRight, color: 'text-green-500', text: `+${diff}%`, bg: 'bg-green-50' };
    if (diff < 0) return { icon: ArrowDownRight, color: 'text-red-500', text: `${diff}%`, bg: 'bg-red-50' };
    return { icon: Minus, color: 'text-gray-500', text: '0%', bg: 'bg-gray-50' };
  };

  const progress = getProgressIndicator();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border">
          <p className="font-medium text-gray-900">{payload[0]?.payload?.company}</p>
          <p className="text-sm text-gray-500">{payload[0]?.payload?.fullDate}</p>
          <p className="text-lg font-bold text-blue-500">{payload[0]?.value}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            {currentUser.picture ? (
              <img 
                src={currentUser.picture} 
                alt={currentUser.name} 
                className="w-16 h-16 rounded-full border-4 border-white/30"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                {currentUser.name?.charAt(0) || 'U'}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{t('welcomeUser', { name: currentUser.name })}</h1>
              <p className="text-white/80">{currentUser.email}</p>
            </div>
          </div>
          <p className="text-white/90">
            {t('viewYourAIJourney')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link 
            to="/quickscan/start"
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
            data-testid="start-assessment-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <ChartBar className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('startFreeAssessmentCard')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('discoverYourScore')}</p>
            <span className="text-blue-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              {t('startNow')} <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          <Link 
            to="/use-cases"
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
            data-testid="explore-usecases-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <Sparkles className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('freeUseCaseHub')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('discoverAIApplications')}</p>
            <span className="text-blue-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              {t('viewUseCasesLink')} <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          <Link 
            to="/roi-compare"
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
            data-testid="roi-compare-card"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('roiComparer')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('compareAIUseCases')}</p>
            <span className="text-green-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              {t('compareNow')} <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>

        {/* User Activity Statistics */}
        {userStats && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8" data-testid="user-stats-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{language === 'nl' ? 'Jouw Activiteit' : 'Your Activity'}</h2>
                <p className="text-sm text-gray-500">
                  {language === 'nl' ? 'Lid sinds' : 'Member since'} {userStats.stats?.created_at ? new Date(userStats.stats.created_at).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { month: 'long', year: 'numeric' }) : '-'}
                </p>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <LogIn className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">{language === 'nl' ? 'Totaal Logins' : 'Total Logins'}</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{userStats.stats?.login_count || 0}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {userStats.stats?.login_frequency_per_week || 0}x {language === 'nl' ? 'per week' : 'per week'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700">{language === 'nl' ? 'Quickscans' : 'Quickscans'}</span>
                </div>
                <p className="text-2xl font-bold text-green-900">{userStats.stats?.completed_assessments || 0}</p>
                <p className="text-xs text-green-600 mt-1">
                  {language === 'nl' ? 'Gemiddelde score' : 'Avg score'}: {userStats.stats?.average_score || 0}%
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bookmark className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">{language === 'nl' ? 'Opgeslagen Cases' : 'Saved Cases'}</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{userStats.stats?.saved_use_cases || 0}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {language === 'nl' ? 'Use cases bewaard' : 'Use cases saved'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span className="text-xs font-medium text-purple-700">{language === 'nl' ? 'Dagen Actief' : 'Days Active'}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">{userStats.stats?.days_since_registration || 0}</p>
                <p className="text-xs text-purple-600 mt-1">
                  {language === 'nl' ? 'Dagen sinds registratie' : 'Days since signup'}
                </p>
              </div>
            </div>

            {/* Activity Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Login Activity by Day */}
              {userStats.activity_by_day && userStats.activity_by_day.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    {language === 'nl' ? 'Activiteit per Dag' : 'Activity by Day'}
                  </h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <BarChart data={userStats.activity_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          formatter={(value) => [value, language === 'nl' ? 'Logins' : 'Logins']}
                        />
                        <Bar dataKey="logins" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Login Methods Pie Chart */}
              {userStats.login_methods && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    {language === 'nl' ? 'Login Methode' : 'Login Method'}
                  </h3>
                  <div className="flex items-center justify-center gap-8">
                    <div className="h-32 w-32">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Email', value: userStats.login_methods.email || 0 },
                              { name: 'Google', value: userStats.login_methods.google || 0 }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={50}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            <Cell fill="#3b82f6" />
                            <Cell fill="#ef4444" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-sm text-gray-600">Email: {userStats.login_methods.email || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-sm text-gray-600">Google: {userStats.login_methods.google || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            {userStats.login_history && userStats.login_history.length > 0 && (
              <div className="mt-6 bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {language === 'nl' ? 'Recente Logins' : 'Recent Logins'}
                </h3>
                <div className="space-y-2">
                  {userStats.login_history.slice(0, 5).map((login, idx) => (
                    <div key={login.timestamp || login.date || `login-${idx}`} className="flex items-center justify-between text-sm py-2 border-b border-gray-200 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${login.method === 'google' ? 'bg-red-100' : 'bg-blue-100'}`}>
                          {login.method === 'google' ? (
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.6 4.6 1.58l3.44-3.44C17.96 1.19 15.22 0 12 0 7.31 0 3.26 2.69 1.28 6.6l4.01 3.11C6.21 6.86 8.87 4.75 12 4.75z"/>
                              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.86 3c2.26-2.09 3.56-5.17 3.56-8.82z"/>
                              <path fill="#34A853" d="M5.29 14.29C5.11 13.56 5 12.79 5 12s.11-1.56.29-2.29L1.28 6.6C.47 8.22 0 10.06 0 12s.47 3.78 1.28 5.4l4.01-3.11z"/>
                              <path fill="#FBBC05" d="M12 24c3.22 0 5.96-1.07 7.95-2.91l-3.86-3c-1.08.72-2.45 1.16-4.09 1.16-3.13 0-5.79-2.11-6.71-4.96l-4.01 3.11C3.26 21.31 7.31 24 12 24z"/>
                            </svg>
                          ) : (
                            <LogIn className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <span className="text-gray-700 capitalize">{login.method}</span>
                      </div>
                      <span className="text-gray-500">
                        {login.timestamp ? new Date(login.timestamp).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Score Trend Badge */}
            {userStats.stats?.score_trend && userStats.stats.score_trend !== 'stable' && (
              <div className="mt-4 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  userStats.stats.score_trend === 'up' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {userStats.stats.score_trend === 'up' ? (
                    <><ArrowUpRight className="h-4 w-4" /> {language === 'nl' ? 'Score stijgt' : 'Score improving'}</>
                  ) : (
                    <><ArrowDownRight className="h-4 w-4" /> {language === 'nl' ? 'Score daalt' : 'Score declining'}</>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Gartner Hype Cycle Section */}
        {!loadingUseCases && allUseCases.length > 0 && (
          <div className="mb-8" data-testid="gartner-hype-section">
            <GartnerHypeCycle 
              savedUseCases={savedUseCases}
              allUseCases={allUseCases}
            />
          </div>
        )}

        {/* Assessment Progress Section - Only show if user has assessments */}
        {assessments.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8" data-testid="progress-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{t('yourAIMaturityProgress')}</h2>
                  <p className="text-sm text-gray-500">{assessments.length} {t('assessmentsCompleted2')}</p>
                </div>
              </div>
              {progress && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${progress.bg}`}>
                  <progress.icon className={`h-5 w-5 ${progress.color}`} />
                  <span className={`font-bold ${progress.color}`}>{progress.text}</span>
                  <span className="text-sm text-gray-500">{t('vsPrevious')}</span>
                </div>
              )}
            </div>

            {/* Progress Chart */}
            {assessments.length >= 2 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Line Chart - Score over time */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium text-gray-700 mb-4">{t('scoreProgression')}</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="score" 
                          stroke="#2563eb" 
                          strokeWidth={3}
                          dot={{ fill: '#2563eb', strokeWidth: 2, r: 6 }}
                          activeDot={{ r: 8, fill: '#1d4ed8' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Bar Chart - Dimension comparison */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium text-gray-700 mb-4">{t('dimensionComparison')}</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <BarChart data={dimensionData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#9ca3af" />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} stroke="#9ca3af" />
                        <Tooltip 
                          formatter={(value, name) => [
                            `${value}%`, 
                            name === 'latest' ? t('latest') : t('previousLabel')
                          ]}
                          labelFormatter={(label) => dimensionData.find(d => d.name === label)?.fullName || label}
                        />
                        <Legend formatter={(value) => value === 'latest' ? t('latest') : t('previousLabel')} />
                        <Bar dataKey="previous" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="latest" fill="#2563eb" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 rounded-xl p-6 text-center">
                <BarChart3 className="h-12 w-12 text-blue-300 mx-auto mb-3" />
                <h3 className="font-medium text-gray-900 mb-2">{t('startSecondAssessment')}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('afterSecondAssessment')}
                </p>
                <Button 
                  onClick={() => navigate('/quickscan/start')}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  {t('startNewAssessment')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Quickscans Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8" data-testid="assessments-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">{t('myAssessmentsTitle')}</h2>
            <Button 
              onClick={() => navigate('/quickscan/start')}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <ChartBar className="h-4 w-4 mr-2" />
              {t('newAssessment')}
            </Button>
          </div>

          {loadingAssessments ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
            </div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">{t('noAssessmentsYetTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {t('startFirstAssessmentDesc')}
              </p>
              <Button 
                onClick={() => navigate('/quickscan/start')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {t('startFreeAssessmentBtn')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {assessments.map((assessment, index) => (
                <div 
                  key={assessment.id || index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => navigate('/quickscan/results', { 
                    state: { 
                      results: {
                        overall_score: assessment.overall_score,
                        dimension_scores: assessment.dimension_scores,
                        company: assessment.company,
                        industry: assessment.industry
                      }
                    } 
                  })}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-xl font-bold text-blue-500">{assessment.overall_score}%</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{assessment.company || t('assessment')}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          {formatDate(assessment.created_at)}
                          {assessment.industry && (
                            <span className="text-gray-400">• {assessment.industry}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {index === 0 && assessments.length > 1 && progress && (
                        <div className={`flex items-center gap-1 px-2 py-1 rounded ${progress.bg}`}>
                          <progress.icon className={`h-4 w-4 ${progress.color}`} />
                          <span className={`text-sm font-medium ${progress.color}`}>{progress.text}</span>
                        </div>
                      )}
                      {index === 0 && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                          {t('latestLabel')}
                        </span>
                      )}
                      <ArrowRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Saved Use Cases Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="saved-usecases-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">{t('mySavedUseCases')}</h2>
            <Button 
              onClick={() => navigate('/use-cases')}
              variant="outline"
            >
              <Bookmark className="h-4 w-4 mr-2" />
              {t('browseFreeUseCases')}
            </Button>
          </div>

          {loadingSaved ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
            </div>
          ) : savedUseCases.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Bookmark className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">{t('noSavedUseCasesTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {t('saveInterestingUseCases')}
              </p>
              <Button 
                onClick={() => navigate('/use-cases')}
                variant="outline"
              >
                {t('discoverFreeUseCases')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedUseCases.map((usecase) => (
                <div 
                  key={usecase.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Link 
                        to={`/use-cases/${usecase.id}`}
                        className="font-medium text-gray-900 hover:text-blue-600 flex items-center gap-1"
                      >
                        {usecase.title}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{usecase.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{usecase.domain}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          usecase.impact === 'high' ? 'bg-green-100 text-green-700' :
                          usecase.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {usecase.impact} {t('impact')}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleUnsave(usecase.id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      title={t('removeFromSavedList')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UserDashboard;
